/**
 * @file ItemStatusBadge.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-24
 * @modified 2026-10-14
 * @description Whether an inventory item is still in use, at a glance.
 *
 *              Two independent flags, and conflating them is the mistake this
 *              component exists to prevent:
 *
 *                is_archived          ARCHIVED here (Search Inventory's
 *                                     Archive). Seeded from Xero's CSV export
 *                                     for older items, but the archive is
 *                                     this system's: since Jade's blend review
 *                                     (14 Oct 2026) an item archived here may
 *                                     still be active in Xero, and no sync
 *                                     brings it back.
 *                is_locally_archived  retired HERE. Local only; no Xero sync
 *                                     path reads or writes it.
 *
 *              Chapple Farm's current blend is archived, so that flag alone
 *              would show the live product as dead. Either flag means
 *              "not in use", and the tooltip says which, so nobody has to guess
 *              why something is greyed out.
 *
 *              A blend awaiting its supplier's code is a third state: it exists
 *              here but not in Xero yet, so it cannot be ordered or invoiced.
 */

import React from 'react';
import './ItemStatusBadge.css';

function ItemStatusBadge({ item, className = '' }) {
  if (!item) return null;

  const archived = !!(item.is_archived ?? item.item_archived);
  const localArchived = !!item.is_locally_archived;
  const awaitingCode = !!item.awaiting_supplier_code;

  if (awaitingCode) {
    return (
      <span className={`item-status-badge is-pending ${className}`.trim()}
            title="Waiting on the supplier's item code. Not in Xero yet, so it cannot be ordered or invoiced.">
        Awaiting code
      </span>
    );
  }

  if (!archived && !localArchived) {
    return (
      <span className={`item-status-badge is-active ${className}`.trim()} title="In use">
        Active
      </span>
    );
  }

  // Name the reason rather than just greying it out — "archived" with no cause
  // sends people to Xero to check, which is exactly the wasted trip this avoids
  // (and Xero may show it active: the archive is this system's).
  const why = archived && localArchived
    ? 'Archived, and retired here as a generation'
    : archived
      ? 'Archived here (it may still be active in Xero)'
      : 'Retired here as a generation (not archived)';

  return (
    <span className={`item-status-badge is-archived ${className}`.trim()} title={why}>
      Archived
    </span>
  );
}

export default ItemStatusBadge;
